import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { SEO } from "../components/SEO";
import { API } from "../lib/site";

const PaymentResult = () => {
    const { pathname, search } = useLocation();
    const isCancel = pathname.includes("cancel");
    const [state, setState] = useState(isCancel ? "cancelled" : "checking");
    const attempts = useRef(0);

    useEffect(() => {
        if (isCancel) return;
        const sessionId = new URLSearchParams(search).get("session_id");
        if (!sessionId) {
            setState("failed");
            return;
        }
        const poll = async () => {
            try {
                const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    setState("paid");
                    return;
                }
            } catch {
                /* keep polling */
            }
            attempts.current += 1;
            if (attempts.current < 15) setTimeout(poll, 2000);
            else setState("timeout");
        };
        poll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <main>
            <SEO title="Payment | Global Green Hotel" description="GGH membership payment status." />
            <div className="grain flex min-h-screen items-center justify-center bg-[#04150D] px-5 py-32">
                <div
                    data-testid="payment-result"
                    className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#07211A] p-10 text-center"
                >
                    {state === "checking" && (
                        <>
                            <Loader2 size={40} className="mx-auto animate-spin text-[#C2A160]" aria-hidden="true" />
                            <h1 className="font-display mt-6 text-2xl text-[#FDFBF7]">Confirming your payment…</h1>
                            <p className="mt-3 text-sm text-[#FDFBF7]/60">This takes a few seconds.</p>
                        </>
                    )}
                    {state === "paid" && (
                        <>
                            <CheckCircle2 size={44} className="mx-auto text-[#D9B96A]" aria-hidden="true" />
                            <h1 className="font-display mt-6 text-3xl text-[#FDFBF7]">Welcome to GGH</h1>
                            <p className="mt-3 text-sm leading-relaxed text-[#FDFBF7]/70">
                                Your payment is confirmed and your membership is active. A
                                confirmation and receipt have been sent to your email.
                            </p>
                            <Link to="/hotel/dashboard" data-testid="payment-goto-dashboard" className="btn-gold mt-8">
                                Go to Your Dashboard
                            </Link>
                        </>
                    )}
                    {(state === "failed" || state === "timeout") && (
                        <>
                            <XCircle size={44} className="mx-auto text-[#E8A0A0]" aria-hidden="true" />
                            <h1 className="font-display mt-6 text-2xl text-[#FDFBF7]">
                                {state === "timeout" ? "Still confirming…" : "Payment not confirmed"}
                            </h1>
                            <p className="mt-3 text-sm leading-relaxed text-[#FDFBF7]/70">
                                Check your dashboard in a moment — your membership activates as soon
                                as the payment is confirmed.
                            </p>
                            <Link to="/hotel/dashboard" data-testid="payment-goto-dashboard-pending" className="btn-outline-light mt-8">
                                Open Dashboard
                            </Link>
                        </>
                    )}
                    {state === "cancelled" && (
                        <>
                            <XCircle size={44} className="mx-auto text-[#FDFBF7]/50" aria-hidden="true" />
                            <h1 className="font-display mt-6 text-2xl text-[#FDFBF7]">Payment cancelled</h1>
                            <p className="mt-3 text-sm leading-relaxed text-[#FDFBF7]/70">
                                No charge was made. You can complete your membership payment anytime
                                from your dashboard.
                            </p>
                            <Link to="/hotel/dashboard" data-testid="payment-goto-dashboard-cancel" className="btn-outline-light mt-8">
                                Back to Dashboard
                            </Link>
                        </>
                    )}
                </div>
            </div>
        </main>
    );
};

export default PaymentResult;
